import React, { useState, useEffect } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, CreditCard, CheckCircle2, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { StripeProvider } from '@/components/billing/StripeProvider';
import { useToast } from '@/hooks/use-toast';

/** DIP: BillingStep uses callbacks only; it does not know which flow (full vs partial) it is in. */

export interface BillingStepCallbacks {
  onSetupIntentRequest: () => Promise<string | null>;
  onPaymentMethodConfirmed: (paymentMethodId: string) => Promise<void>;
  onSkip?: () => Promise<void>;
}

interface BillingStepFormProps {
  onPaymentMethodConfirmed: (paymentMethodId: string) => Promise<void>;
  onSkip?: () => Promise<void>;
  allowSkip: boolean;
  isGuided: boolean;
}

const BillingStepForm: React.FC<BillingStepFormProps> = ({
  onPaymentMethodConfirmed,
  onSkip,
  allowSkip,
  isGuided,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements || !termsAccepted || isLoading) return;
    setIsLoading(true);
    try {
      const { setupIntent, error: confirmError } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });
      if (confirmError) {
        // If the SetupIntent already succeeded (e.g. double-click), extract
        // the payment method from the error payload and continue normally.
        const si = (confirmError as unknown as Record<string, unknown>)?.setup_intent as
          | { status?: string; payment_method?: string | { id?: string } }
          | undefined;
        if (
          confirmError.code === 'setup_intent_unexpected_state' &&
          si?.status === 'succeeded' &&
          si?.payment_method
        ) {
          const pmId =
            typeof si.payment_method === 'string'
              ? si.payment_method
              : si.payment_method?.id;
          if (pmId) {
            await onPaymentMethodConfirmed(pmId);
            toast({
              title: 'Billing Activated',
              description: 'Your payment method has been saved. You can continue to your dashboard.',
              variant: 'success',
            });
            return;
          }
        }
        toast({
          title: 'Card Error',
          description: confirmError.message || 'Failed to verify card',
          variant: 'destructive',
        });
        return;
      }
      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent?.payment_method as { id?: string })?.id;
      if (!paymentMethodId) throw new Error('No payment method returned');
      await onPaymentMethodConfirmed(paymentMethodId);
      toast({
        title: 'Billing Activated',
        description: 'Your payment method has been saved. You can continue to your dashboard.',
        variant: 'success',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    if (!onSkip) return;
    setIsSkipping(true);
    try {
      await onSkip();
      toast({
        title: 'Billing setup skipped',
        description: 'You can set up billing later from Settings.',
        variant: 'default',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div className="text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-teal/10">
          <CreditCard className="h-7 w-7 text-brand-teal" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Set Up Monthly Billing</h2>
        <p className="mt-2 text-sm text-gray-500">
          Usage-based billing — you only pay for services rendered.
        </p>
      </div>

      <Collapsible defaultOpen={false}>
        <Card className="border-gray-200 bg-white shadow-sm">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full text-left">
              <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base text-gray-900">Service Pricing</CardTitle>
                    <p className="text-xs text-gray-500 mt-1">
                      Usage-based pricing — platform fee included. Click to view details.
                    </p>
                  </div>
                  <ChevronDown className="h-5 w-5 text-gray-400 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </div>
              </CardHeader>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-3 pt-0">
              {!isGuided && (
                <>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">MDI Visit</p>
                      <p className="text-xs text-gray-500">Medical Director Involvement</p>
                    </div>
                    <Badge className="bg-brand-teal/10 text-brand-teal border-0">$50/visit</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">Consulting Visit</p>
                      <p className="text-xs text-gray-500">Consulting physician visit</p>
                    </div>
                    <Badge className="bg-brand-teal/10 text-brand-teal border-0">$75/visit</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">Lab Order</p>
                      <p className="text-xs text-gray-500">Lab order processing fee</p>
                    </div>
                    <Badge className="bg-brand-teal/10 text-brand-teal border-0">$150/order</Badge>
                  </div>
                </>
              )}
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900">Platform Fee</p>
                  <p className="text-xs text-gray-500">NovaMed platform access</p>
                </div>
                <Badge className="bg-emerald-50 text-emerald-600 border-0">Included</Badge>
              </div>
              <p className="text-center text-xs text-gray-400">
                Invoiced on the 1st of each month for services used in the prior month.
              </p>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <Card className="border-gray-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-900">Payment Method</CardTitle>
          <CardDescription className="text-gray-500">
            Your card will be charged monthly for services used.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-gray-200 p-4">
            <PaymentElement options={{ layout: 'tabs' }} />
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="billing-terms"
              checked={termsAccepted}
              onCheckedChange={(c) => setTermsAccepted(c === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="billing-terms"
              className="text-sm leading-relaxed text-gray-600 cursor-pointer"
            >
              I agree to the <a href="https://www.nimbus-os.com/terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 underline hover:text-teal-700">NovaMed Billing Terms</a> and authorize monthly charges to the card above
              for services rendered by my clinic.
            </label>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !termsAccepted || !stripe}
            className="w-full bg-brand-teal hover:bg-brand-teal/90 text-white shadow-lg shadow-brand-teal/25"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Activating...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Save Card & Continue
              </>
            )}
          </Button>
          {allowSkip && onSkip && (
            <div className="text-center">
              <button
                type="button"
                onClick={handleSkip}
                disabled={isSkipping}
                className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700"
              >
                {isSkipping ? 'Skipping...' : 'Skip for now — set up within 7 days'}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export interface BillingStepProps extends BillingStepCallbacks {
  allowSkip: boolean;
  isGuided?: boolean;
}

export const BillingStep: React.FC<BillingStepProps> = ({
  onSetupIntentRequest,
  onPaymentMethodConfirmed,
  onSkip,
  allowSkip,
  isGuided = false,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    onSetupIntentRequest()
      .then((secret) => {
        if (!cancelled && secret) setClientSecret(secret);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.message || 'Failed to load payment form');
          toast({
            title: 'Error',
            description: err?.message || 'Failed to load payment form',
            variant: 'destructive',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onSetupIntentRequest, toast]);

  if (loadError && !clientSecret) {
    return (
      <div className="mx-auto max-w-lg space-y-4 text-center py-8">
        <p className="text-sm text-gray-600">{loadError}</p>
        {onSkip && allowSkip && (
          <button
            type="button"
            onClick={() => onSkip().catch(() => {})}
            className="text-sm text-brand-teal underline underline-offset-2"
          >
            Skip for now
          </button>
        )}
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
      </div>
    );
  }

  return (
    <StripeProvider clientSecret={clientSecret}>
      <BillingStepForm
        onPaymentMethodConfirmed={onPaymentMethodConfirmed}
        onSkip={onSkip}
        allowSkip={allowSkip}
        isGuided={isGuided}
      />
    </StripeProvider>
  );
};

export default BillingStep;
